import React from 'react';
import { Container, Typography, Box, Stack, Button, Grid, Card, CardContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { trackSignupConversion } from '../utils/analytics';

const SectionContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(6, 0),
    background: '#FFFFFF',
    position: 'relative',
    overflow: 'hidden',
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(8, 0),
    },
    [theme.breakpoints.up('lg')]: {
        padding: theme.spacing(10, 0),
    },
}));

const PopularCardWrapper = styled(Box)(() => ({
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: -8,
        left: -8,
        right: -8,
        bottom: -8,
        background: 'linear-gradient(90deg, #44ff9a -0.55%, #44b0ff 22.86%, #8b44ff 48.36%, #ff6644 73.33%, #ebff70 99.34%)',
        borderRadius: 16,
        opacity: 0.3,
        filter: 'blur(12px)',
        zIndex: 0,
    },
}));

const PricingCard = styled(Card)<{ popular?: boolean }>(({ popular }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    background: popular ? '#111827' : '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: 'none',
    zIndex: 1,
}));

const PriceTypography = styled(Typography)<{ popular?: boolean }>(({ popular }) => ({
    fontWeight: 700,
    color: popular ? '#FFFFFF' : '#111827',
    fontSize: '1.5rem',
    lineHeight: 1,
}));

const FeatureItem = ({ text, popular }: { text: string; popular?: boolean }) => (
    <Typography 
        variant="body2" 
        sx={{ 
            fontSize: '0.75rem',
            fontWeight: 500,
            color: popular ? '#9CA3AF' : '#6B7280',
            lineHeight: 1.7,
        }}
    >
        {text}
    </Typography>
);

const plans = [
    {
        title: 'Starter',
        price: '$0',
        period: '/month',
        description: 'Perfect for small businesses just getting started with reviews.',
        features: [
            'Up to 50 review requests/month',
            'Basic email templates',
            'AI response generator',
            'Standard support',
            '1 Team member',
        ],
        buttonText: 'Start for Free',
        buttonVariant: 'outlined' as const,
    },
    {
        title: 'Growth',
        price: '$49',
        period: '/month',
        description: 'Ideal for growing businesses ready to scale their review strategy.',
        features: [
            'Up to 500 review requests/month',
            'Advanced email templates',
            'AI Survey and Response generator',
            'Email & SMS campaigns',
            'Priority support',
            '3 Team members',
            'customer rewards'
        ],
        buttonText: 'Launching Soon',
        buttonVariant: 'outlined' as const,
    },
    {
        title: 'Pro',
        price: '$99',
        period: '/month',
        description: 'Everything you need to grow your reputation and sales.',
        popular: true,
        features: [
            'Up to 5000 review requests/month',
            'Advanced AI Survey & response generator',
            'SMS & Email campaigns',
            'Custom branding',
            'multiple platform integration',
            'Priority support',
            '5 Team members',
            'customer rewards'
        ],
        buttonText: 'Launching Soon',
        buttonVariant: 'contained' as const,
    },
    {
        title: 'Enterprise',
        price: 'Custom',
        period: '',
        description: 'Advanced features and control for large organizations.',
        features: [
            'Everything in Pro',
            'Multi-location management',
            'API access',
            'Dedicated success manager',
            'SSO & Advanced security',
            'Unlimited team members',
            'Custom contract & invoicing',
            'Review widgets for website',
        ],
        buttonText: 'Launching Soon',
        buttonVariant: 'outlined' as const,
    },
];

const PricingSection: React.FC = () => {
    const navigate = useNavigate();

    const handleButtonClick = (planTitle: string) => {
        if (planTitle === 'Starter') {
            // Track signup conversion before navigating
            trackSignupConversion(navigate, '/signup');
        }
        // For other plans, you can add additional logic here if needed
    };

    return (
        <SectionContainer id="pricing">
            <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, lg: 4 }, maxWidth: '1400px' }}>
                <Box textAlign="center" sx={{ mb: { xs: 4, md: 8 }, maxWidth: 600, mx: 'auto', px: { xs: 2, md: 0 } }}>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 700,
                            mb: 1.5,
                            color: '#064E3B',
                            fontSize: { xs: '1.875rem', sm: '2.25rem', lg: '3rem' },
                        }}
                    >
                        Make the wise decision for your business
                    </Typography>
                    <Typography 
                        variant="h6" 
                        sx={{ 
                            color: '#6B7280',
                            fontSize: '1.125rem',
                            fontWeight: 400,
                            mt: 1.5,
                        }}
                    >
                        Choose from our affordable 4 packages
                    </Typography>
                </Box>

                <Grid container spacing={1} alignItems="stretch" sx={{ mt: { xs: 2, md: 4 } }}>
                    {plans.map((plan, index) => {
                        const CardWrapper = plan.popular ? PopularCardWrapper : Box;
                        return (
                            <Grid item xs={12} sm={6} md={3} key={index} sx={{ minWidth: 0, flex: '1 1 0%' }}>
                                <CardWrapper>
                                    <PricingCard popular={plan.popular}>
                                        <CardContent sx={{ p: { xs: 1.25, sm: 1.5, md: 1.75 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: 700, 
                                                    mb: 0.25, 
                                                    color: plan.popular ? '#FFFFFF' : '#111827',
                                                    fontSize: '0.9375rem',
                                                }}
                                            >
                                                {plan.title}
                                            </Typography>
                                            
                                            <PriceTypography popular={plan.popular} sx={{ mt: 0.25, mb: 0.5 }}>
                                                {plan.price}
                                            </PriceTypography>
                                            
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: plan.popular ? '#9CA3AF' : '#6B7280',
                                                    mb: 1,
                                                    fontSize: '0.75rem',
                                                    lineHeight: 1.8,
                                                    minHeight: 40,
                                                }}
                                            >
                                                {plan.description}
                                            </Typography>

                                            <Button
                                                variant={plan.popular ? 'contained' : 'outlined'}
                                                fullWidth
                                                onClick={() => handleButtonClick(plan.title)}
                                                sx={{
                                                    py: 0.875,
                                                    mt: 1,
                                                    borderRadius: 2,
                                                    fontWeight: 700,
                                                    fontSize: '0.8125rem',
                                                    textTransform: 'none',
                                                    ...(plan.popular
                                                        ? {
                                                            bgcolor: '#FFFFFF',
                                                            color: '#111827',
                                                            border: '2px solid transparent',
                                                            '&:hover': {
                                                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                                                            },
                                                        }
                                                        : {
                                                            borderColor: '#9CA3AF',
                                                            borderWidth: 2,
                                                            color: '#111827',
                                                            '&:hover': {
                                                                bgcolor: '#111827',
                                                                color: '#FFFFFF',
                                                                borderColor: '#111827',
                                                            },
                                                        }
                                                    ),
                                                }}
                                            >
                                                {plan.buttonText}
                                            </Button>

                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    fontWeight: 700,
                                                    color: plan.popular ? '#FFFFFF' : '#111827',
                                                    mt: 1,
                                                    mb: 0.5,
                                                    fontSize: '0.8125rem',
                                                }}
                                            >
                                                What's included:
                                            </Typography>

                                            <Stack spacing={1.5} sx={{ mt: 0.5, flexGrow: 1 }}>
                                                {plan.features.slice(0, 5).map((feature, idx) => (
                                                    <FeatureItem key={idx} text={feature} popular={plan.popular} />
                                                ))}
                                            </Stack>
                                        </CardContent>
                                    </PricingCard>
                                </CardWrapper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Container>
        </SectionContainer>
    );
};

export default PricingSection;
